import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import KeycloakService from '../../../services/KeycloakService';
import { getManagerProjects, getUserData } from '../../../services/UserService';
import {
  approveRemoteOfficeHoursTask,
  decideRemoteOfficeHoursQuota,
  getRemoteOfficeHoursQuotaRequestsForManager,
  getRemoteOfficeHoursTasksForManager,
} from '../../../services/RemoteOfficeHoursService';
import { ERROR_KEY, SHOW_NOTIFICATION } from '../../../constants';
import PaginationComponent from '../../../components/Pagination/Pagination';
import RemoteOfficeHoursQuotaCard from '../../../components/RemoteOfficeHoursQuota/RemoteOfficeHoursQuotaCard';
import RemoteOfficeHoursQuotaFilters from '../../../components/RemoteOfficeHoursQuota/RemoteOfficeHoursQuotaFilters';
import RemoteOfficeHoursTaskCard from '../../../components/RemoteOfficeHoursTaskCard/RemoteOfficeHoursTaskCard';
import RemoteOfficeHoursTaskFilters from '../../../components/RemoteOfficeHoursTaskCard/RemoteOfficeHoursTaskFilters';

const defaultFillFilters = () => ({
  startDate: moment().subtract(1, 'month').format('YYYY-MM-DD'),
  endDate: moment().format('YYYY-MM-DD'),
  projectId: '',
  status: 'PENDING',
});

const defaultQuotaFilters = () => ({
  status: 'PENDING',
  projectId: '',
  quotaMonth: '',
  empId: '',
});

const ManageRemoteOfficeHoursTasksComponent = () => {
  const managerId = KeycloakService.getUserDetail();
  const dispatch = useDispatch();
  const [tab, setTab] = useState(0);
  const [tasks, setTasks] = useState([]);
  const [quotaRequests, setQuotaRequests] = useState([]);
  const [employees, setEmployees] = useState({});
  const [projects, setProjects] = useState([]);
  const [fillFilters, setFillFilters] = useState(defaultFillFilters);
  const [quotaFilters, setQuotaFilters] = useState(defaultQuotaFilters);
  const [fillPage, setFillPage] = useState(1);
  const [fillPageNo, setFillPageNo] = useState(0);
  const [fillPageSize, setFillPageSize] = useState(5);
  const [fillTotalPages, setFillTotalPages] = useState(0);
  const [quotaPage, setQuotaPage] = useState(1);
  const [quotaPageNo, setQuotaPageNo] = useState(0);
  const [quotaPageSize, setQuotaPageSize] = useState(5);
  const [quotaTotalPages, setQuotaTotalPages] = useState(0);
  const [refresh, setRefresh] = useState(false);
  const loadRequestRef = useRef(0);

  const notify = (type, message) => {
    dispatch({
      type: SHOW_NOTIFICATION,
      data: { open: true, type, message },
    });
  };

  const getProjectName = (projectId) => {
    const match = projects.find(
      (p) => String(p.projectId || p.id) === String(projectId),
    );
    return match?.projectName || match?.name || '';
  };

  const loadProjects = async () => {
    const response = await getManagerProjects(managerId, null, null, 0);
    if (response?.status === 200) {
      setProjects(response.data?.content || []);
    }
  };

  const loadEmployeeNames = async (content) => {
    const empMap = {};
    await Promise.all(
      content.map(async (item) => {
        const empId = item.employeeId;
        if (!empId || empMap[empId]) {
          return;
        }
        try {
          const user = await getUserData(empId);
          if (!user.isError) {
            empMap[empId] = `${user.data.firstName} ${user.data.lastName}`;
          }
        } catch {
          // fallback to employeeId
        }
      }),
    );
    setEmployees((prev) => ({ ...prev, ...empMap }));
  };

  const loadTasks = useCallback(async () => {
    const requestId = ++loadRequestRef.current;
    const response = await getRemoteOfficeHoursTasksForManager(
      managerId,
      fillFilters,
      fillPageNo,
      fillPageSize,
    );
    if (requestId !== loadRequestRef.current) {
      return;
    }
    if (!response.isError) {
      const pageData = response.data?.data || {};
      const content = pageData.content || [];
      setTasks(content);
      setFillTotalPages(pageData.totalPages || 0);
      await loadEmployeeNames(content);
    }
  }, [fillFilters, fillPageNo, fillPageSize, managerId]);

  const loadQuotaRequests = useCallback(async () => {
    const response = await getRemoteOfficeHoursQuotaRequestsForManager(
      managerId,
      quotaFilters,
      quotaPageNo,
      quotaPageSize,
    );
    if (!response.isError) {
      const pageData = response.data?.data || {};
      const content = pageData.content || [];
      setQuotaRequests(content);
      setQuotaTotalPages(pageData.totalPages || 0);
      await loadEmployeeNames(content);
    }
  }, [managerId, quotaFilters, quotaPageNo, quotaPageSize]);

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    if (tab === 0) {
      loadQuotaRequests();
    } else {
      loadTasks();
    }
  }, [tab, loadQuotaRequests, loadTasks, refresh]);

  useEffect(() => {
    setQuotaPageNo(0);
    setQuotaPage(1);
    setRefresh((prev) => !prev);
  }, [quotaFilters]);

  useEffect(() => {
    setFillPageNo(0);
    setFillPage(1);
    setRefresh((prev) => !prev);
  }, [fillFilters]);

  const handleQuotaAction = async (request, status) => {
    const response = await decideRemoteOfficeHoursQuota({
      id: request.id,
      employeeId: request.employeeId,
      status,
      approverId: managerId,
    });
    if (!response.isError) {
      notify('success', `Quota ${status.toLowerCase()}`);
      setRefresh((prev) => !prev);
    } else {
      notify(ERROR_KEY, response.message);
    }
  };

  const handleTaskAction = async (task, status) => {
    const payload = {
      ...task,
      status,
      approverId: managerId,
    };
    const response = await approveRemoteOfficeHoursTask(payload);
    if (!response.isError) {
      notify('success', `Fill ${status.toLowerCase()}`);
      setRefresh((prev) => !prev);
    } else {
      notify(ERROR_KEY, response.message);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Remote Office Hours Approvals
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Quota requests are project-scoped — only PMs of the selected project see them here.
        Hours fills require the selected project&apos;s manager.
      </Typography>
      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Quota requests" />
        <Tab label="Hours fills" />
      </Tabs>

      {tab === 0 && (
        <>
          <RemoteOfficeHoursQuotaFilters
            filters={quotaFilters}
            setFilters={setQuotaFilters}
            projects={projects}
          />
          {quotaRequests.length === 0 ? (
            <Typography>No Remote Office quota requests found.</Typography>
          ) : (
            quotaRequests.map((req) => (
              <RemoteOfficeHoursQuotaCard
                key={req.id}
                request={req}
                variant="manager"
                projectName={getProjectName(req.projectId)}
                employeeName={employees[req.employeeId]}
                onApprove={(item) => handleQuotaAction(item, 'APPROVED')}
                onReject={(item) => handleQuotaAction(item, 'REJECTED')}
              />
            ))
          )}
          {quotaRequests.length > 0 && (
            <Box className="page-holder">
              <PaginationComponent
                count={quotaTotalPages}
                page={quotaPage}
                pageChange={(e, value) => {
                  setQuotaPageNo(value - 1);
                  setQuotaPage(value);
                  setRefresh((prev) => !prev);
                }}
                pageSize={quotaPageSize}
                handlePageSizeChange={(event) => {
                  setQuotaPageSize(event.target.value);
                  setQuotaPageNo(0);
                  setQuotaPage(1);
                  setRefresh((prev) => !prev);
                }}
              />
            </Box>
          )}
        </>
      )}

      {tab === 1 && (
        <>
          <RemoteOfficeHoursTaskFilters
            filters={fillFilters}
            setFilters={setFillFilters}
            projects={projects}
          />
          {tasks.length === 0 ? (
            <Typography>No Remote Office Hours fills found.</Typography>
          ) : (
            tasks.map((task) => (
              <RemoteOfficeHoursTaskCard
                key={task.id}
                task={task}
                variant="manager"
                employeeName={employees[task.employeeId]}
                onApprove={(item) => handleTaskAction(item, 'APPROVED')}
                onReject={(item) => handleTaskAction(item, 'REJECTED')}
              />
            ))
          )}
          {tasks.length > 0 && (
            <Box className="page-holder">
              <PaginationComponent
                count={fillTotalPages}
                page={fillPage}
                pageChange={(e, value) => {
                  setFillPageNo(value - 1);
                  setFillPage(value);
                  setRefresh((prev) => !prev);
                }}
                pageSize={fillPageSize}
                handlePageSizeChange={(event) => {
                  setFillPageSize(event.target.value);
                  setFillPageNo(0);
                  setFillPage(1);
                  setRefresh((prev) => !prev);
                }}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ManageRemoteOfficeHoursTasksComponent;
